"use client"

import { useEffect, useState, useMemo } from "react"
import type { Project } from "@/lib/types"
import { getUserProjects } from "@/services/project-service"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  PlusCircle,
  Search,
  SortAsc,
  SortDesc,
  Archive,
  FolderOpen,
  X,
  Folder,
  Calendar,
  Clock,
} from "lucide-react"
import { ProjectCard } from "@/components/project/project-card"
import { ProjectCardSkeleton } from "@/components/project/project-card-skeleton"
import { CreateProjectDialog } from "@/components/project/CreateProjectDialog"
import { useMobile } from "@/hooks/use-mobile"
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export default function ProjectsPage() {
  const [projects, setProjects] = useState<(Project & { preview_count: number })[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "archived" | "empty">("all")
  const [sortBy, setSortBy] = useState<"name" | "created_at" | "updated_at">("updated_at")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const isMobile = useMobile()
  const { t, i18n } = useTranslation(["common", "profile"]);

  const fetchProjects = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const userProjects = await getUserProjects()
      setProjects(userProjects)
    } catch (err) {
      console.error("Error fetching projects:", err)
      setError("Failed to load projects. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(query) ||
          (project.description && project.description.toLowerCase().includes(query)),
      )
    }    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((project) => {
        if (statusFilter === "active") return !project.is_archived
        if (statusFilter === "archived") return project.is_archived
        if (statusFilter === "empty") return project.preview_count === 0
        return true
      })
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | Date
      let bValue: string | Date

      switch (sortBy) {
        case "name":
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case "created_at":
          aValue = new Date(a.created_at)
          bValue = new Date(b.created_at)
          break
        case "updated_at":
        default:
          aValue = new Date(a.updated_at)
          bValue = new Date(b.updated_at)
          break
      }

      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

    return sorted
  }, [projects, searchQuery, statusFilter, sortBy, sortOrder])
  const handleProjectCreated = (newProject: Project) => {
    setProjects((prevProjects) => [{ ...newProject, preview_count: 0 }, ...prevProjects])
  }

  const handleProjectUpdated = (updatedProject: Project) => {
    setProjects((prevProjects) =>
      prevProjects.map((p) =>
        p.id === updatedProject.id
          ? { ...updatedProject, preview_count: p.preview_count }
          : p
      )
    )
  }

  const handleProjectDeleted = (projectId: string) => {
    setProjects((prevProjects) => prevProjects.filter((p) => p.id !== projectId))
  }

  const toggleSortOrder = () => {
    setSortOrder((current) => (current === "asc" ? "desc" : "asc"))
  }
  // Get project counts for filter badges
  const projectCounts = useMemo(() => {
    const active = projects.filter((p) => !p.is_archived).length
    const archived = projects.filter((p) => p.is_archived).length
    const empty = projects.filter((p) => p.preview_count === 0).length
    return { active, archived, empty, total: projects.length }
  }, [projects])

  const hasActiveFilters = searchQuery.trim() || statusFilter !== "all"

  const clearAllFilters = () => {
    setSearchQuery("")
    setStatusFilter("all")
  }

  const getSortLabel = () => {
    switch (sortBy) {
      case "name":
        return "Name"
      case "created_at":
        return "Date Created"
      case "updated_at":
        return "Last Updated"
      default:
        return "Last Updated"
    }
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 container mx-auto px-4 py-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <div className="text-destructive mb-4">
                <Folder className="h-12 w-12 mx-auto mb-2 opacity-50" />
              </div>
              <h3 className="font-semibold mb-2">
                {t("common:project-page.loading.unable")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchProjects} variant="outline">
                {t("common:project-page.loading.try")}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">
              {t("common:project-page.header.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("common:project-page.header.subtitle")}
            </p>
          </div>
          <CreateProjectDialog
            onProjectCreated={handleProjectCreated}
            trigger={
              <Button size={isMobile ? "default" : "lg"} className="w-full sm:w-auto">
                <PlusCircle className="mr-2 h-4 w-4" />
                {t("common:project-page.header.createProject")}
              </Button>
            }
          />
        </div>

        {/* Search and Filter Section */}
        {!isLoading && projects.length > 0 && (
          <Card className="border-0 p-0">
            <CardContent className="p-0 space-y-4">
              {/* Search Bar */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder={t("common:project-page.search.placeholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-9"
                  />
                </div>
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-3 shrink-0"
                    onClick={() => setSearchQuery("")}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Filter and Sort Controls */}
              <div
                className={cn(
                  "flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between",
                  i18n.language === "ar" ? "rtl" : "ltr"
                )}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  {/* Status Filter */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={statusFilter}
                      onValueChange={(value: "all" | "active" | "archived" | "empty") => setStatusFilter(value)}
                    >
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse text-right"
                                : "flex-row text-left"
                            )}
                          >
                            <FolderOpen className="h-4 w-4" />
                            <span>{t("common:project-page.filter.all")}</span>
                            <Badge
                              variant="secondary"
                              className={
                                i18n.language === "ar" ? "mr-auto" : "ml-auto"
                              }
                            >
                              {projectCounts.total}
                            </Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="active">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse text-right"
                                : "flex-row text-left"
                            )}
                          >
                            <Folder className="h-4 w-4 text-green-600" />
                            <span>
                              {t("common:project-page.filter.active")}
                            </span>
                            <Badge variant="secondary" className="ml-auto">
                              {projectCounts.active}
                            </Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="archived">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse text-right"
                                : "flex-row text-left"
                            )}
                          >
                            <Archive className="h-4 w-4 text-orange-600" />
                            <span>
                              {t("common:project-page.filter.archived")}
                            </span>
                            <Badge variant="secondary" className="ml-auto">
                              {projectCounts.archived}
                            </Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="empty">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse text-right"
                                : "flex-row text-left"
                            )}
                          >
                            <FolderOpen className="h-4 w-4 text-muted-foreground" />
                            <span>{t("common:project-page.filter.empty")}</span>
                            <Badge variant="secondary" className="ml-auto">
                              {projectCounts.empty}
                            </Badge>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort Controls */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={sortBy}
                      onValueChange={(value: "name" | "created_at" | "updated_at") => setSortBy(value)}
                    >
                      <SelectTrigger className="w-full sm:w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="updated_at">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse"
                                : "flex-row"
                            )}
                          >
                            <Clock className="h-4 w-4" />
                            {t("common:project-page.sort.lastUpdated")}
                          </div>
                        </SelectItem>
                        <SelectItem value="created_at">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse"
                                : "flex-row"
                            )}
                          >
                            <Calendar className="h-4 w-4" />
                            {t("common:project-page.sort.dateCreated")}
                          </div>
                        </SelectItem>
                        <SelectItem value="name">
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              i18n.language === "ar"
                                ? "flex-row-reverse"
                                : "flex-row"
                            )}
                          >
                            <Folder className="h-4 w-4" />
                            {t("common:project-page.sort.name")}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={toggleSortOrder}
                      title={`Sort ${getSortLabel()} ${sortOrder === "asc" ? "ascending" : "descending"}`}
                    >
                      {sortOrder === "asc" ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {/* Results and Clear Filters */}
                <div
                  className={cn(
                    "flex items-center justify-between gap-4",
                    i18n.language === "ar" ? "flex-row-reverse" : "flex-row"
                  )}
                >
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">
                      {filteredAndSortedProjects.length}
                    </span>{" "}
                    of <span className="font-medium">{projects.length}</span>{" "}
                    {t("common:project-page.results.projects")}
                  </div>

                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="gap-2"
                    >
                      <X className="h-3 w-3" />
                      {t("common:project-page.results.clearFilters")}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <ProjectCardSkeleton key={`skeleton-${i}`} />
              ))}
          </div>
        )}

        {/* Empty State - No Projects */}
        {!isLoading && projects.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                <FolderOpen className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">
                {t("common:project-page.empty.noProjects.title")}
              </h3>
              <p className="mb-6 text-muted-foreground max-w-sm mx-auto">
                {t("common:project-page.empty.noProjects.description")}
              </p>
              <CreateProjectDialog
                onProjectCreated={handleProjectCreated}
                trigger={
                  <Button size="lg">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {t("common:project-page.empty.noProjects.create")}
                  </Button>
                }
              />
            </CardContent>
          </Card>
        )}

        {/* Empty State - No Results */}
        {!isLoading && projects.length > 0 && filteredAndSortedProjects.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                <Search className="h-10 w-10 text-muted-foreground" />
              </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("common:project-page.empty.noResults.title")}
                </h3>
                <p className="mb-6 text-muted-foreground max-w-sm mx-auto">
                  {t("common:project-page.empty.noResults.description")}
                </p>
                <Button variant="outline" onClick={clearAllFilters}>
                  <X className="mr-2 h-3 w-3" />
                  {t("common:project-page.empty.noResults.clearFilters")}
                </Button>
              </CardContent>
            </Card>
          )}

        {/* Projects Grid */}
        {!isLoading && filteredAndSortedProjects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onProjectDeleted={handleProjectDeleted}
                onProjectUpdated={handleProjectUpdated}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
